import React from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {ErrorBoundary} from 'app/general/structures/ErrorBoundary';
import {FlexPanel} from 'app/general/structures/FlexPanel';
import {SPHeaderStructure} from 'app/general/structures/SPFrame/SPHeader/SPHeaderStructure';
import {SPPage} from 'app/general/structures/SPPage';
import {EditableLogo} from 'app/general/widgets/EditableLogo';
import {GeneralEditField} from 'app/general/widgets/GeneralEditField';
import {
  useAccountSettingsState,
  useUpdateAccountSettings,
} from 'app/settings/api';
import {
  DOCUMENTS_HOME,
  HOME_WELCOME_IMPORT,
  HOME_WELCOME_INVITATIONS,
  HOME_WELCOME_TEAMS,
} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {ArrowProgressBar} from './ArrowProgress';

import type {WithRouterProps} from 'react-router';

type ChildProps = {
  onNext: (location: string) => unknown;
};

type Props = {
  children: (props: ChildProps) => React.ReactNode;
  currentStep: number;
};

export const WelcomeStructure = ({
  currentStep,
  children,
  router,
}: WithRouterProps & Props) => {
  const steps = [
    {name: gt('Procedures and Policies'), url: HOME_WELCOME_IMPORT},
    {name: gt('Add teams'), url: HOME_WELCOME_TEAMS},
    {name: gt('Invite Teammates'), url: HOME_WELCOME_INVITATIONS},
    {name: gt('Use SweetProcess'), url: DOCUMENTS_HOME},
  ];
  const settings = useAccountSettingsState().data;
  const [updateAccountSettings] = useUpdateAccountSettings();
  if (settings == null) {
    return null;
  }
  const name = settings.account_name;
  const isDefaultName = settings.company_name != settings.account_name;
  const displayName = isDefaultName ? `${name.split(' ')[0]}'s Company` : name;

  const updateAccountName = (company_name: string) =>
    updateAccountSettings({
      company_name,
      account_id: settings.account_id,
    }).unwrap();

  const updateCompanyLogo = (company_logo: string) =>
    updateAccountSettings({
      company_logo,
      account_id: settings.account_id,
    });

  const goNext = (location: string) => router.push(location);

  return (
    <>
      <FlexPanel
        type="vertical"
        secondary={
          <ErrorBoundary name="sp-header">
            <SPHeaderStructure
              companyLogo={<EditableLogo onUpdate={updateCompanyLogo} />}
            >
              <div className="col d-flex align-items-center text-white">
                <GeneralEditField
                  canEdit={true}
                  inputClass={'col-auto'}
                  editButtonInputClass={'text-white'}
                  fieldName={'company_name'}
                  isInline={true}
                  fieldValue={displayName}
                  updateField={updateAccountName}
                  editTitle={gt('Set your company name')}
                  editPlaceholder={gt('Company Name')}
                />
              </div>
            </SPHeaderStructure>
          </ErrorBoundary>
        }
        secondaryZindex="10"
      >
        <SPPage
          actionArea={null}
          pageActions={null}
          pageTitle={gt('Setting up your SweetProcess Account.')}
          breadcrumbProps={{docName: steps[currentStep].name}}
        >
          <div className="mx-auto mt-2">
            <ArrowProgressBar steps={steps} currentStep={currentStep} />
          </div>
          <div className="mx-auto col-sm-12 col-xl-10">
            <WelcomePanel className="p-3">
              <h2>
                {currentStep + 1}. {steps[currentStep].name}
              </h2>
              {children({onNext: goNext})}
            </WelcomePanel>
          </div>
        </SPPage>
      </FlexPanel>
    </>
  );
};

const WelcomePanel = styled.div`
  text-align: left;
  display: inline-block;
  vertical-align: top;
  width: 100%;
  border-radius: 4px;
  box-shadow: 0 1px 30px rgba(0, 0, 0, 0.1);
`;

export const SmallTextSpan = styled.span`
  font-size: 0.8rem;
  color: ${colors.darkGray};

  &:before {
    content: '* ';
  }
`;
